import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../lib/api';

/**
 * Hook für Job-Status-Tracking mit SSE-Unterstützung
 */
export function useJobStatus(jobId) {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);

  // SSE-Verbindung für Echtzeit-Updates
  const [eventSource, setEventSource] = useState(null);

  // Job-Status abrufen
  const fetchJobStatus = useCallback(async () => {
    if (!jobId) return;

    try {
      const response = await apiClient.get(`/notifications/job/${jobId}`);
      const data = response.data;

      if (data.success) {
        setJob(data.data);
        setLogs(data.data.logs || []);
        setError(null);
      } else {
        setError(data.error || 'Unbekannter Fehler');
      }
    } catch (err) {
      console.error('Fehler beim Abrufen des Job-Status:', err);
      const message = err?.response?.data?.message || err?.response?.data?.error || err.message;
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  // SSE-Verbindung für Echtzeit-Updates (Cookie-Auth; Browser unterstützen keine Custom-Headers)
  const setupSSE = useCallback(() => {
    if (!jobId || eventSource) return;

    const es = new EventSource('/api/notifications/stream');

    es.onopen = () => {
      console.log('🔔 SSE-Verbindung etabliert');
    };

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'job_update' && data.jobId === jobId) {
          console.log('📊 Job-Update erhalten:', data);
          fetchJobStatus();
        } else if (data.type === 'job_log' && data.jobId === jobId) {
          console.log('📝 Job-Log erhalten:', data);
          setLogs(prev => [...prev, data.log]);
        } else if (data.type === 'heartbeat') {
          console.log('💓 SSE Heartbeat');
        }
      } catch (err) {
        console.error('Fehler beim Parsen der SSE-Nachricht:', err);
      }
    };

    es.onerror = (error) => {
      console.error('❌ SSE-Verbindungsfehler:', error);
      setTimeout(() => {
        if (es.readyState === EventSource.CLOSED) {
          console.log('🔄 Versuche SSE-Reconnect...');
          setupSSE();
        }
      }, 5000);
    };

    setEventSource(es);
  }, [jobId, eventSource, fetchJobStatus]);

  // Initiale Daten laden
  useEffect(() => {
    if (jobId) {
      fetchJobStatus();
    }
  }, [jobId, fetchJobStatus]);

  // SSE-Verbindung aufbauen
  useEffect(() => {
    if (jobId && job?.status === 'processing') {
      setupSSE();
    }
  }, [jobId, job?.status, setupSSE]);

  // Cleanup bei Unmount
  useEffect(() => {
    return () => {
      if (eventSource) {
        eventSource.close();
        setEventSource(null);
      }
    };
  }, [eventSource]);

  // Job abbrechen
  const cancelJob = useCallback(async () => {
    if (!jobId) return false;

    try {
      const response = await apiClient.delete(`/jobs/${jobId}`);
      if (response.data?.success) {
        await fetchJobStatus();
        return true;
      }
      return false;
    } catch (err) {
      console.error('Fehler beim Abbrechen des Jobs:', err);
      return false;
    }
  }, [jobId, fetchJobStatus]);

  return {
    job,
    loading,
    error,
    logs,
    refetch: fetchJobStatus,
    cancelJob,
    isCompleted: job?.status === 'completed',
    isFailed: job?.status === 'failed',
    isProcessing: job?.status === 'processing',
    isPending: job?.status === 'pending'
  };
}

/**
 * Hook für Job-Liste
 * @param {boolean} fetchAllForAdmin — Admins: alle Mandanten-Jobs via GET /api/jobs/all
 */
export function useJobList(fetchAllForAdmin = false) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);

  const fetchJobs = useCallback(async (status = null, limit = 20, offset = 0) => {
    try {
      setLoading(true);

      if (fetchAllForAdmin) {
        const response = await apiClient.get('/jobs/all');

        if (response.status === 403) {
          throw new Error('Keine Berechtigung für die globale Job-Liste');
        }

        const data = response.data;

        if (data.success) {
          let list = data.data.jobs || [];
          if (status) list = list.filter((j) => j.status === status);
          setJobs(list);
          setPagination(null);
          setError(null);
        } else {
          setError(data.error || 'Unbekannter Fehler');
        }
      } else {
        const params = { limit, offset };
        if (status) params.status = status;

        const response = await apiClient.get('/jobs', { params });
        const data = response.data;

        if (data.success) {
          setJobs(data.data.jobs);
          setPagination(data.data.pagination);
          setError(null);
        } else {
          setError(data.error || 'Unbekannter Fehler');
        }
      }
    } catch (err) {
      console.error('Fehler beim Abrufen der Jobs:', err);
      const message = err?.response?.data?.message || err?.response?.data?.error || err.message;
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [fetchAllForAdmin]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  return {
    jobs,
    loading,
    error,
    pagination,
    refetch: fetchJobs
  };
}
